import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import type { SpendFilters, SpendStatus, SpendType } from '@/features/accounting-spend/api'
import { ExpenseForm } from '@/features/accounting-spend/expense-form'
import { SpendListScreen } from '@/features/accounting-spend/spend-list-screen'
import { hasGrant } from '@/lib/me'

const isSpendType = (value: unknown): value is SpendType =>
  typeof value === 'string' && ['expense', 'card_credit'].includes(value)
const isSpendStatus = (value: unknown): value is SpendStatus =>
  typeof value === 'string' && ['draft', 'posted', 'void'].includes(value)

/**
 * `/accounting/expenses/new` keeps the expense dialog over its list. The
 * list's URL search state therefore survives opening and closing the dialog.
 */
export const Route = createFileRoute('/_authenticated/accounting/expenses/new')({
  validateSearch: (search: Record<string, unknown>): SpendFilters => ({
    type: isSpendType(search.type) ? search.type : undefined,
    status: isSpendStatus(search.status) ? search.status : undefined,
    print_later: search.print_later === true || search.print_later === '1' ? true : undefined,
    from: typeof search.from === 'string' ? search.from : undefined,
    to: typeof search.to === 'string' ? search.to : undefined,
    payee: typeof search.payee === 'string' ? search.payee : undefined,
    payment_account:
      typeof search.payment_account === 'string' ? search.payment_account : undefined,
    search: typeof search.search === 'string' ? search.search : undefined,
  }),
  beforeLoad: ({ context, search }) => {
    if (!hasGrant(context.queryClient, 'accounting_money_document.create')) {
      throw redirect({ to: '/accounting/expenses', search })
    }
  },
  component: ExpensesNewRoute,
})

function ExpensesNewRoute() {
  const navigate = useNavigate()
  const search = Route.useSearch()

  return (
    <>
      <SpendListScreen
        params={search}
        onParamsChange={(next) => void navigate({ to: '/accounting/expenses/new', search: next })}
      />
      <ExpenseForm
        open
        onOpenChange={(open) => {
          if (!open) void navigate({ to: '/accounting/expenses', search })
        }}
      />
    </>
  )
}
